/**
 * CaseSearchFilters.tsx
 * 
 * Search and filter controls for FI Investigation page
 * Allows filtering by member, institution, status, classification, date range
 */

import { FC } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';

export interface FilterState {
  searchQuery: string;
  status: string;
  classification: string;
  institutionName: string;
  dateFrom: string;
  dateTo: string;
}

interface CaseSearchFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

const CaseSearchFilters: FC<CaseSearchFiltersProps> = ({
  filters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  isLoading = false
}) => {
  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filters, searchQuery: value });
  };

  const handleStatusChange = (value: string) => {
    onFilterChange({ ...filters, status: value });
  };

  const handleClassificationChange = (value: string) => {
    onFilterChange({ ...filters, classification: value });
  };

  const handleInstitutionChange = (value: string) => {
    onFilterChange({ ...filters, institutionName: value });
  };

  const handleDateFromChange = (value: string) => {
    onFilterChange({ ...filters, dateFrom: value });
  };

  const handleDateToChange = (value: string) => {
    onFilterChange({ ...filters, dateTo: value });
  };

  const hasActiveFilters = 
    filters.searchQuery || 
    filters.status || 
    filters.classification || 
    filters.institutionName ||
    filters.dateFrom ||
    filters.dateTo;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
          <FiSearch size={20} />
          Search & Filter
        </h3>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <FiX size={16} />
            Clear All
          </button>
        )}
      </div>

      {/* Search Box */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Search by Member Name, Email, or Institution
        </label>
        <div className="relative">
          <input
            type="text"
            value={filters.searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="e.g., John Smith, john@email.com, Chase Bank..."
            className="w-full px-4 py-2 pl-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && onApplyFilters()}
          />
          <FiSearch className="absolute left-3 top-3 text-slate-400" size={18} />
        </div>
      </div>

      {/* Filter Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="investigating">Investigating</option>
            <option value="waiting_for_member">Waiting for Member</option>
            <option value="waiting_for_plaid">Waiting for Plaid</option>
            <option value="monitoring">Monitoring</option>
            <option value="resolved">Resolved</option>
            <option value="closed_no_action">Closed (No Action)</option>
          </select>
        </div>

        {/* Classification Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Classification</label>
          <select
            value={filters.classification}
            onChange={(e) => handleClassificationChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Classifications</option>
            <option value="member_credentials">Member Credentials</option>
            <option value="member_mfa">Member MFA</option>
            <option value="member_abandoned">Member Abandoned</option>
            <option value="institution_degraded">Institution Degraded</option>
            <option value="institution_down">Institution Down</option>
            <option value="institution_unsupported">Institution Unsupported</option>
          </select>
        </div>

        {/* Date From */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">From Date</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleDateFromChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Date To */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">To Date</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleDateToChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Apply Button */}
      <div className="flex justify-end">
        <button
          onClick={onApplyFilters}
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          <FiSearch size={16} />
          {isLoading ? 'Searching...' : 'Apply Filters'}
        </button>
      </div>
    </div>
  );
};

export default CaseSearchFilters;
